import { useState, useEffect } from "react";
import { useQueryClient } from "react-query";
import { uniqBy } from "lodash";
import {
  archiveStaffOfficePolicies,
  getAssigneeStaff,
  policyAssignmentAction,
} from "repositories/office-policy-management-repository";
import constants from "../../../../constants";
import toast from "react-hot-toast";
import { getStorage, setStorage } from "utils";
import {
  getOfficeNavigationContext,
  isInOfficeNavigationContext,
  OFFICE_NAV_CONTEXT_CHANGE_EVENT,
} from "utils/officeNavigationContext";

const pageSize = 10;
const STAFF_OFFICE_POLICY_FILTER_CACHE_KEY =
  constants.officePolicies.cache.staffOfficePolicyFilter;

/** @returns {import("utils").StorageValue & { userReset?: boolean }} */
function getCachedStaffOfficePolicyFilters() {
  return getStorage(STAFF_OFFICE_POLICY_FILTER_CACHE_KEY) || {};
}

/**
 * Office navigation context pre-fills the office filter on first visit.
 * After an explicit reset (`userReset`), cached empty filters take precedence.
 */
function resolveInitialOfficeFilters() {
  const cacheFilters = getCachedStaffOfficePolicyFilters();
  if (cacheFilters.userReset) {
    return cacheFilters.offices || [];
  }
  const officeContext = getOfficeNavigationContext();
  if (isInOfficeNavigationContext(officeContext) && officeContext?.officeId) {
    return [Number(officeContext.officeId)];
  }
  return cacheFilters.offices || [];
}

const useStaffOfficePolicyModule = (
  useAllOfficePolicies,
  isArchive = false,
  accountOwnerId,
) => {
  const cacheFilters = getCachedStaffOfficePolicyFilters();
  const initialOfficeFilters = resolveInitialOfficeFilters();
  const [showLoader, setShowLoader] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [officePolicies, setOfficePolicies] = useState([]);
  const [appliedOfficeFilters, setAppliedOfficeFilters] = useState(
    initialOfficeFilters,
  );
  const [appliedAssignorFilter, setAppliedAssignorFilter] = useState(
    cacheFilters?.staff || [],
  );
  const [appliedStatusFilters, setAppliedStatusFilters] = useState(
    cacheFilters?.status || [],
  );
  const [appliedPriorityFilters, setAppliedPriorityFilters] = useState(
    cacheFilters?.priority || [],
  );
  const [searchValue, setSearchValue] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isAssigneeModal, setIsAssigneeModal] = useState(false);
  const [assigneeList, setAssigneeList] = useState([]);
  const [officeFilter, setOfficeFilter] = useState(initialOfficeFilters);
  const [staffFilter, setStaffFilter] = useState(cacheFilters?.staff || []);
  const [statusFilter, setStatusFilter] = useState(cacheFilters?.status || []);
  const [priorityFilter, setPriorityFilter] = useState(
    cacheFilters?.priority || [],
  );
  const [priority, setPriority] = useState({ id: 4, name: "All" });
  const [status, setStatus] = useState({ value: "", label: "All" });
  const [sortBy, setSortByState] = useState(2);
  const queryClient = useQueryClient();

  const { isLoading, data, refetch } = useAllOfficePolicies(
    pageNumber,
    pageSize,
    appliedOfficeFilters,
    appliedAssignorFilter,
    searchValue,
    isArchive,
    appliedPriorityFilters,
    appliedStatusFilters,
    sortBy,
    accountOwnerId,
    { enabled: true },
  );

  useEffect(() => {
    if (pageNumber === 1) {
      if (!isLoading && data && data.items) {
        setOfficePolicies(uniqBy([...data?.items], "id"));
        setTotalItems(data?.pagination?.totalItems);
        setTotalPages(data?.pagination?.totalPages);
      }
    } else if (!isLoading && data && data.items) {
      setOfficePolicies((prev) => uniqBy([...prev, ...data?.items], "id"));
      setTotalItems(data?.pagination?.totalItems);
      setTotalPages(data?.pagination?.totalPages);
    }
  }, [isLoading, data, pageNumber]);

  useEffect(() => {
    const cached = getCachedStaffOfficePolicyFilters();
    if (cached.userReset) return;

    const context = getOfficeNavigationContext();
    if (isInOfficeNavigationContext(context) && context?.officeId) {
      const officeId = Number(context.officeId);
      setOfficeFilter([officeId]);
      setAppliedOfficeFilters([officeId]);
      setPageNumber(1);
    }
  }, []);

  useEffect(() => {
    setPageNumber(1);
    setOfficePolicies([]);
  }, [isArchive]);

  useEffect(() => {
    const syncOfficeFiltersFromContext = () => {
      const context = getOfficeNavigationContext();
      if (isInOfficeNavigationContext(context) && context?.officeId) {
        const officeId = Number(context.officeId);
        setOfficeFilter([officeId]);
        setAppliedOfficeFilters([officeId]);
        setPageNumber(1);
        setStorage(STAFF_OFFICE_POLICY_FILTER_CACHE_KEY, {
          ...getCachedStaffOfficePolicyFilters(),
          offices: [officeId],
          userReset: false,
        });
        return;
      }
      const cached = getCachedStaffOfficePolicyFilters();
      setOfficeFilter(cached?.offices || []);
      setAppliedOfficeFilters(cached?.offices || []);
      setPageNumber(1);
    };
    window.addEventListener(
      OFFICE_NAV_CONTEXT_CHANGE_EVENT,
      syncOfficeFiltersFromContext,
    );
    return () =>
      window.removeEventListener(
        OFFICE_NAV_CONTEXT_CHANGE_EVENT,
        syncOfficeFiltersFromContext,
      );
  }, []);

  useEffect(() => {
    setPageNumber(1);
    if (priority.id) {
      if (priority.id < 4) {
        setAppliedPriorityFilters([priority.id]);
        setPriorityFilter([priority.id]);
      } else {
        setAppliedPriorityFilters([]);
        setPriorityFilter([]);
      }
    }
    if (status.value) {
      setAppliedStatusFilters([status.value]);
      setStatusFilter([status.value]);
    } else {
      setAppliedStatusFilters([]);
      setStatusFilter([]);
    }
  }, [priority, status]);

  useEffect(() => {
    queryClient.invalidateQueries(["/officePolicies/mypolicy"]);
    refetch();
  }, [isArchive, sortBy, appliedPriorityFilters, appliedStatusFilters, accountOwnerId]);

  const handleSearchTerm = (e) => {
    const value = e.target.value;
    setInputValue(value);
    if (value.length >= 3) {
      setPageNumber(1);
      setSearchValue(value);
    } else if (value.length === 0) {
      setPageNumber(1);
      setSearchValue("");
    }
  };

  const StaffMembers = async (officePolicyId) => {
    setShowLoader(true);
    try {
      const res = await getAssigneeStaff(officePolicyId, 10, 1);
      const list = res?.items ?? (Array.isArray(res) ? res : []);
      if (list.length) setAssigneeList(list);
      setShowLoader(false);
    } catch (e) {
      setShowLoader(false);
    }
  };

  const handleToggleAssigne = (officePolicyId = "") => {
    if (isAssigneeModal) {
      setIsAssigneeModal(false);
      setAssigneeList([]);
    } else {
      StaffMembers(officePolicyId);
      setIsAssigneeModal(true);
    }
  };

  const resetFilter = () => {
    setOfficeFilter([]);
    setStaffFilter([]);
    setStatusFilter([]);
    setPriorityFilter([]);
    setAppliedOfficeFilters([]);
    setAppliedAssignorFilter([]);
    setAppliedStatusFilters([]);
    setAppliedPriorityFilters([]);
    setPriority({ id: 4, name: "All" });
    setStatus({ value: "", label: "All" });
    setStorage(STAFF_OFFICE_POLICY_FILTER_CACHE_KEY, {
      offices: [],
      staff: [],
      status: [],
      priority: [],
      userReset: true,
    });
    setPageNumber(1);
  };

  const handleApplyFilters = () => {
    setAppliedOfficeFilters(officeFilter);
    setAppliedAssignorFilter(staffFilter);
    setAppliedStatusFilters(statusFilter);
    setAppliedPriorityFilters(priorityFilter);
    setStorage(STAFF_OFFICE_POLICY_FILTER_CACHE_KEY, {
      offices: officeFilter,
      staff: staffFilter,
      status: statusFilter,
      priority: priorityFilter,
      userReset: false,
    });
    setPageNumber(1);
  };

  const handleActionOfficePolicy = async (
    officePolicyId,
    officePolicyAction,
    param,
  ) => {
    setShowLoader(true);
    try {
      const res = await policyAssignmentAction(
        officePolicyId,
        officePolicyAction,
        param,
      );
      if (res?.status) {
        setPageNumber(1);
        toast.success(res.message);
        if (officePolicyAction === "Accept") {
          setOfficePolicies((prev) =>
            prev.map((p) =>
              p.id === officePolicyId ? { ...p, status: 3 } : p,
            ),
          );
        } else if (officePolicyAction === "Start") {
          setOfficePolicies((prev) =>
            prev.map((p) =>
              p.id === officePolicyId ? { ...p, status: 4 } : p,
            ),
          );
        } else if (officePolicyAction === "Reject") {
          setOfficePolicies((prev) =>
            prev.map((p) =>
              p.id === officePolicyId ? { ...p, status: 6 } : p,
            ),
          );
        } else {
          setOfficePolicies((prev) =>
            prev.map((p) =>
              p.id === officePolicyId ? { ...p, status: 5 } : p,
            ),
          );
        }
        return res;
      }
    } catch (e) {
      toast.error(e.message);
      return { status: false, message: e.message };
    } finally {
      setShowLoader(false);
    }
  };

  const handleArchiveApi = async (officePolicyAssignmentId, action) => {
    try {
      const res = await archiveStaffOfficePolicies(
        officePolicyAssignmentId,
        action,
      );
      if (res?.status) {
        toast.success(res.message);
        setOfficePolicies((prev) =>
          prev?.filter((p) => p?.id !== officePolicyAssignmentId),
        );
      }
    } catch (e) {
      setShowLoader(false);
    }
  };

  /**
   * Sort changes always restart at page 1. Infinite scroll then requests later pages.
   * @param {number} nextSortBy
   */
  const setSortBy = (nextSortBy) => {
    window.scrollTo(0, 0);

    const sortChanged = nextSortBy !== sortBy;
    if (!sortChanged && pageNumber === 1) return;

    setPageNumber(1);
    if (sortChanged) setSortByState(nextSortBy);
  };

  const isAnyLoading = isLoading || showLoader;
  const isFilterApply =
    appliedOfficeFilters?.length > 0 ||
    appliedAssignorFilter?.length > 0 ||
    appliedStatusFilters?.length > 0 ||
    appliedPriorityFilters?.length > 0;

  return {
    isLoading: isAnyLoading,
    officePolicies,
    totalItems,
    searchValue,
    inputValue,
    isAssigneeModal,
    assigneeList,
    showLoader,
    officeFilter,
    staffFilter,
    statusFilter,
    priorityFilter,
    isFilterApply,
    sortBy,
    priority,
    status,
    pageNumber,
    totalPages,
    setStatus,
    setPriority,
    setOfficePolicies,
    setPageNumber,
    handleSearchTerm,
    handleToggleAssigne,
    handleActionOfficePolicy,
    setAppliedOfficeFilters,
    setAppliedAssignorFilter,
    setAppliedStatusFilters,
    setAppliedPriorityFilters,
    resetFilter,
    setOfficeFilter,
    setStaffFilter,
    setStatusFilter,
    setPriorityFilter,
    handleApplyFilters,
    handleArchiveApi,
    setSortBy,
  };
};

export default useStaffOfficePolicyModule;
